import React, { useState } from 'react';
import { Button } from '../ui';

/**
 * Estimate status options
 */
const ESTIMATE_STATUSES = {
  draft: { label: 'Draft', color: '#6080a0', bg: 'rgba(96,128,160,0.15)' },
  sent: { label: 'Sent', color: '#f0a030', bg: 'rgba(240,160,48,0.15)' },
  accepted: { label: 'Accepted', color: '#40c060', bg: 'rgba(64,192,96,0.15)' },
};

/**
 * EstimateList - Modal for viewing and managing project estimates
 */
const EstimateList = ({
  isOpen,
  onClose,
  estimates = [],
  onNew,
  onEdit,
  onDuplicate,
  onDelete,
  onStatusChange,
  onConvertToInvoice,
  isMobile = false,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleDelete = (estimate) => {
    if (confirmDelete === estimate.id) {
      onDelete?.(estimate.id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(estimate.id);
      // Auto-clear confirmation after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleDuplicate = (estimate) => {
    const duplicated = {
      ...estimate,
      id: undefined, // Will be regenerated
      name: `${estimate.name} (Copy)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onDuplicate?.(duplicated);
  };

  const cycleStatus = (estimate) => {
    const statuses = ['draft', 'sent', 'accepted'];
    const currentIndex = statuses.indexOf(estimate.status || 'draft');
    const nextStatus = statuses[(currentIndex + 1) % statuses.length];
    onStatusChange?.(estimate.id, nextStatus);
  };

  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        width: isMobile ? '100%' : '700px',
        maxHeight: isMobile ? '100%' : '80vh',
        borderRadius: isMobile ? 0 : '12px',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '20px' }}>📋</span>
            <span style={styles.headerTitle}>Project Estimates</span>
            <span style={styles.count}>({estimates.length})</span>
          </div>
          <div style={styles.headerRight}>
            <Button
              variant="primary"
              onClick={onNew}
              style={{ background: '#00c8ff', color: '#000', fontWeight: '600' }}
            >
              + New Estimate
            </Button>
            <button style={styles.closeButton} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {estimates.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📝</div>
              <div style={styles.emptyTitle}>No Estimates Yet</div>
              <div style={styles.emptyText}>
                Create estimates from the Materials Calculator or start a new blank estimate.
              </div>
              <Button
                variant="primary"
                onClick={onNew}
                style={{ marginTop: '16px', background: '#00c8ff', color: '#000' }}
              >
                Create First Estimate
              </Button>
            </div>
          ) : (
            <div style={styles.list}>
              {estimates.map((estimate) => {
                const status = ESTIMATE_STATUSES[estimate.status] || ESTIMATE_STATUSES.draft;
                const isDeleting = confirmDelete === estimate.id;

                return (
                  <div key={estimate.id} style={styles.estimateCard}>
                    <div style={styles.cardMain}>
                      {/* Left: Info */}
                      <div style={styles.cardInfo}>
                        <div style={styles.estimateName}>{estimate.name || 'Untitled Estimate'}</div>
                        <div style={styles.estimateMeta}>
                          {estimate.clientName && (
                            <span style={styles.metaItem}>
                              <span style={styles.metaIcon}>👤</span>
                              {estimate.clientName}
                            </span>
                          )}
                          <span style={styles.metaItem}>
                            <span style={styles.metaIcon}>📅</span>
                            {formatDate(estimate.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Middle: Total */}
                      <div style={styles.cardTotal}>
                        <div style={styles.totalLabel}>Total</div>
                        <div style={styles.totalAmount}>{formatCurrency(estimate.total)}</div>
                      </div>

                      {/* Right: Status */}
                      <button
                        style={{
                          ...styles.statusBadge,
                          background: status.bg,
                          color: status.color,
                          borderColor: status.color,
                        }}
                        onClick={() => cycleStatus(estimate)}
                        title="Click to change status"
                      >
                        {status.label}
                      </button>
                    </div>

                    {/* Actions */}
                    <div style={styles.cardActions}>
                      <button
                        style={styles.actionButton}
                        onClick={() => onEdit?.(estimate)}
                        title="Edit estimate"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        style={styles.actionButton}
                        onClick={() => handleDuplicate(estimate)}
                        title="Create a copy"
                      >
                        📄 Duplicate
                      </button>
                      <button
                        style={{
                          ...styles.actionButton,
                          ...styles.invoiceButton,
                        }}
                        onClick={() => onConvertToInvoice?.(estimate)}
                        title="Convert to invoice"
                      >
                        🧾 Invoice
                      </button>
                      <button
                        style={{
                          ...styles.actionButton,
                          ...styles.deleteButton,
                          ...(isDeleting ? styles.deleteConfirm : {}),
                        }}
                        onClick={() => handleDelete(estimate)}
                        title={isDeleting ? 'Click again to confirm' : 'Delete estimate'}
                      >
                        {isDeleting ? '⚠️ Confirm?' : '🗑️ Delete'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerInfo}>
            {estimates.length > 0 && (
              <>
                <span>Total estimates: {estimates.length}</span>
                <span style={styles.footerDivider}>|</span>
                <span>
                  Combined value: {formatCurrency(estimates.reduce((sum, e) => sum + (e.total || 0), 0))}
                </span>
              </>
            )}
          </div>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: '#1a2332',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,200,255,0.05)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerTitle: {
    color: '#00c8ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  count: {
    color: '#6080a0',
    fontSize: '14px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  closeButton: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '4px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  emptyText: {
    color: '#6080a0',
    fontSize: '14px',
    maxWidth: '300px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  estimateCard: {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  cardMain: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    gap: '16px',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  estimateName: {
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  estimateMeta: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#6080a0',
    fontSize: '12px',
  },
  metaIcon: {
    fontSize: '11px',
  },
  cardTotal: {
    textAlign: 'right',
    minWidth: '100px',
  },
  totalLabel: {
    color: '#6080a0',
    fontSize: '10px',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  totalAmount: {
    color: '#00c8ff',
    fontSize: '18px',
    fontWeight: '700',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  cardActions: {
    display: 'flex',
    gap: '1px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    background: 'rgba(0,0,0,0.2)',
  },
  actionButton: {
    flex: 1,
    padding: '10px',
    background: 'transparent',
    border: 'none',
    color: '#6080a0',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  invoiceButton: {
    color: '#0088ff',
  },
  deleteButton: {
    color: '#a06060',
  },
  deleteConfirm: {
    background: 'rgba(255,80,80,0.2)',
    color: '#ff6060',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)',
  },
  footerInfo: {
    color: '#6080a0',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerDivider: {
    opacity: 0.5,
  },
};

export default EstimateList;
